import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import CancelIcon from '@mui/icons-material/Cancel';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import InvDataGrid from '../../components/InvDataGrid';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import FilterToolbar from '../../components/FilterToolbar';
import ConfirmDialog from '../../components/ConfirmDialog';
import { getRfqs, publishRfq, cancelRfq } from '../../api/rfq';
import { fromApiRfq } from '../../utils/rfqEntity';
import { RFQ_STATUSES, RFQ_TYPES } from '../../constants/rfqManagement';
import { formatDate } from '../../utils';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

const PAGE_SIZE = 25;

export default function RfqList() {
  const { can } = useInvRole();
  const { url } = useRouteMatch();
  const history = useHistory();
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [actionTarget, setActionTarget] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actioning, setActioning] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = { limit: 100 };
      if (statusFilter) p.status = statusFilter;
      if (typeFilter) p.rfq_type = typeFilter;
      if (search) p.search = search;
      const data = await getRfqs(p);
      const list = Array.isArray(data) ? data : (data?.items || data?.data || []);
      setRfqs(list.map(fromApiRfq));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, search]);

  useEffect(() => { load(); }, [load]);

  const newRfqUrl = useMemo(() => url.replace('/rfqs', '/rfq/new'), [url]);

  const hasActiveFilters = Boolean(searchInput.trim() || statusFilter || typeFilter);

  function handleResetFilters() {
    setSearchInput('');
    setStatusFilter('');
    setTypeFilter('');
  }

  async function executeAction() {
    const required = actionType === 'publish' ? INV_ACTIONS.RFQ_PUBLISH : INV_ACTIONS.RFQ_CANCEL;
    if (!can(required)) return;
    setActioning(true);
    try {
      const rfqId = actionTarget.id || actionTarget.rfq_id;
      if (actionType === 'publish') await publishRfq(rfqId);
      else if (actionType === 'cancel') await cancelRfq(rfqId);
      setActionTarget(null);
      toast.success(actionType === 'publish' ? 'RFQ published.' : 'RFQ cancelled.');
      load();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally {
      setActioning(false);
    }
  }

  const columns = [
    { field: 'rfq_number', headerName: 'RFQ #', width: 140 },
    { field: 'rfq_title', headerName: 'Title', flex: 1, minWidth: 180,
      valueGetter: (p) => p.row.rfq_title || p.row.title },
    { field: 'rfq_type', headerName: 'Type', width: 120 },
    { field: 'submission_deadline', headerName: 'Deadline', width: 120,
      valueFormatter: (p) => formatDate(p.value || p.row.deadline) },
    { field: 'status', headerName: 'Status', width: 130,
      renderCell: (p) => <StatusChip status={p.value === 'OPEN' ? 'PUBLISHED' : p.value} /> },
    { field: 'created_at', headerName: 'Created', width: 120,
      valueFormatter: (p) => formatDate(p.value) },
    { field: 'actions', headerName: '', width: 150, sortable: false,
      renderCell: (p) => {
        const rfqId = p.row.id || p.row.rfq_id;
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="View">
              <IconButton size="small" onClick={() => history.push(`${url}/${rfqId}`)}
                sx={{ color: IPD_COLORS.primary }}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {p.row.status === 'DRAFT' && (
              <>
                {can(INV_ACTIONS.RFQ_EDIT) && (
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => history.push(`${url}/${rfqId}/edit`)}
                      sx={{ color: IPD_COLORS.textSecondary }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {can(INV_ACTIONS.RFQ_PUBLISH) && (
                  <Tooltip title="Publish">
                    <IconButton size="small"
                      onClick={() => { setActionTarget(p.row); setActionType('publish'); }}
                      sx={{ color: IPD_COLORS.success.text }}>
                      <PublishIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
            {['DRAFT', 'PUBLISHED', 'OPEN'].includes(p.row.status) && can(INV_ACTIONS.RFQ_CANCEL) && (
              <Tooltip title="Cancel">
                <IconButton size="small"
                  onClick={() => { setActionTarget(p.row); setActionType('cancel'); }}
                  sx={{ color: IPD_COLORS.error.text }}>
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      } },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="Material Management"
        title="Request for Quotation"
        description={`${rfqs.length} RFQs`}
        actions={can(INV_ACTIONS.RFQ_CREATE) ? (
          <InvButton startIcon={<AddIcon />} onClick={() => history.push(newRfqUrl)}>
            New RFQ
          </InvButton>
        ) : null}
      />


      <FilterToolbar>
        <FilterToolbar.Search
          placeholder="RFQ number or title…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <FilterToolbar.Filter
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={[
            { value: '', label: 'All' },
            ...RFQ_STATUSES.map((s) => ({ value: s, label: s.replace(/_/g, ' ') })),
          ]}
        />
        <FilterToolbar.Filter
          label="Type"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          options={[
            { value: '', label: 'All' },
            ...RFQ_TYPES.map((t) => ({ value: t.value, label: t.label })),
          ]}
        />
        <FilterToolbar.Reset onClick={handleResetFilters} disabled={!hasActiveFilters} />
      </FilterToolbar>

      <InvDataGrid
        fillHeight
        rows={rfqs}
        columns={columns}
        loading={loading}
        error={error}
        onRetry={load}
        getRowId={(r) => r.id || r.rfq_id}
        emptyMessage="No RFQs found."
        pageSize={PAGE_SIZE}
        emptyAction={can(INV_ACTIONS.RFQ_CREATE) ? (
          <InvButton startIcon={<AddIcon />} onClick={() => history.push(newRfqUrl)}>
            Create RFQ
          </InvButton>
        ) : null}
      />

      <ConfirmDialog
        open={Boolean(actionTarget)}
        loading={actioning}
        title={actionType === 'publish' ? 'Publish RFQ' : 'Cancel RFQ'}
        description={actionType === 'publish'
          ? `Publish "${actionTarget?.rfq_number}"? Vendors will be able to submit quotes.`
          : `Cancel "${actionTarget?.rfq_number}"? This cannot be undone.`}
        destructive={actionType === 'cancel'}
        onConfirm={executeAction}
        onCancel={() => setActionTarget(null)}
      />
    </Box>
  );
}
